import React from 'react';
import * as PropTypes from 'prop-types';
import moment from 'moment';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import MultiSelect from 'UI/multiSelect';
import Checkbox from 'UI/checkbox';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from 'UI/button';
import Footer from 'UI/footer';
import LabelInput from 'UI/labelInput';
import WarningIcon from '@material-ui/icons/Warning';
import clsx from 'clsx';

const Customisations = ({
  classes,
  hotelCategory,
  paceOfTrip,
  tripTypes,
  tripTypesList,
  occasions,
  occasionList,
  foodPreference,
  foodPreferencesList,
  mealPlan,
  mealPlanList,
  transferMode,
  transferModeList,
  clientPreferences,
  totalBudget,
  customBudget,
  visaAddon,
  cruiseAddon,
  splitStay,
  cruiseName,
  cruiseNights,
  cruiseNamesData,
  cruiseNightsData,
  tripDates,
  places,
  errorMsg,
  isLoading,
  itinerary,
  hasEdits,
  enableRoutePlan,
  onChange,
  onRemoveHandler,
  onItemSelection,
  onValidityChecker,
  onSubmitHandler,
  onTabChange,
}) => {
  // Check if Singapore is selected as a destination
  const isSingaporeSelected = places?.items?.some(
    (item) => item.name?.toLowerCase() === 'singapore'
  ) || false;
  // Check if Maldives is selected as a destination
  const isMaldivesSelected = places?.items?.some(
    (item) => item.countryName?.toLowerCase() === 'maldives'
  ) || false;
  return (
    <div className={classes.body}>
      {/* Hotel Category Row */}
      <div className={classes.row} style={{ alignItems: 'flex-start', marginBottom: 0 }}>
        <div className={classes.customizationSection}>
          <Typography className={classes.sectionLabel}>HOTEL CATEGORY</Typography>
          <div className={classes.optionButtonsContainer}>
            {['3 Stars', '4 Stars', '5 Stars'].map((category) => (
              <ButtonBase
                key={category}
                className={clsx(
                  classes.optionButton,
                  hotelCategory === category && classes.selectedOption
                )}
                onClick={() => onChange({ field: 'hotelCategory', value: category })}
              >
                <Typography className={classes.optionText}>
                  {category}
                </Typography>
              </ButtonBase>
            ))}
          </div>
          <br />
          <br />
          {isMaldivesSelected && (
            <>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                <MultiSelect
                  label="MEAL PLAN (Select all preferred)"
                  selections={mealPlan.items}
                  value={mealPlan.value}
                  extraClass={classes.mealPlanSelector}
                  inputClass={classes.input}
                  data={mealPlanList}
                  accessor="title"
                  removeHandler={(item) => onRemoveHandler({ field: 'mealPlan', accessor: 'title', item })}
                  onChange={(val) => onChange({ field: 'mealPlan', value: val })}
                  onSelected={(item) => onItemSelection({ field: 'mealPlan', accessor: 'title', item })}
                />
                <MultiSelect
                  label="TRANSFER MODE (Select all preferred)"
                  selections={transferMode.items}
                  value={transferMode.value}
                  extraClass={classes.mealPlanSelector}
                  inputClass={classes.input}
                  data={transferModeList}
                  accessor="title"
                  removeHandler={(item) => onRemoveHandler({ field: 'transferMode', accessor: 'title', item })}
                  onChange={(val) => onChange({ field: 'transferMode', value: val })}
                  onSelected={(item) => onItemSelection({ field: 'transferMode', accessor: 'title', item })}
                />
              </div>
              <br />
              <br />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <div className={classes.splitStayLabel}>SPLIT STAY</div>
                <div className={classes.splitStayCheckbox}>
                  <Checkbox
                    checked={splitStay || false}
                    onChange={(e) => onChange({ field: 'splitStay', value: e.target.checked })}
                    color="primary"
                    style={{ padding: 0, marginRight: 8 }}
                  />
                  <Typography>Split stay between different room types</Typography>
                </div>
              </div>
              <br />
              <br />
            </>
          )}
          <Typography className={classes.sectionLabel}>Budget</Typography>
          <div className={classes.optionButtonsContainer}>
            {['Cheap', 'Mid-Range', 'High-end', 'Enter Custom'].map((budgetOption) => (
              <ButtonBase
                key={budgetOption}
                className={clsx(
                  classes.optionButton,
                  totalBudget === budgetOption && classes.selectedOption
                )}
                onClick={() => onChange({ field: 'totalBudget', value: budgetOption })}
              >
                <Typography className={classes.optionText}>
                  {budgetOption}
                </Typography>
              </ButtonBase>
            ))}
          </div>
          {totalBudget === 'Enter Custom' && (
            <div style={{ width: '50%', marginTop: 10 }} className={classes.wrapperRight}>
              <LabelInput
                placeholder="Maximum budget (Rs)"
                value={customBudget}
                onChange={(val) => onChange({ field: 'customBudget', value: val })}
              />
            </div>
          )}
          <br />
          <br />
          <Typography className={classes.sectionLabel}>INCLUDE ADD ONS</Typography>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visaAddon || false}
                onChange={(e) => onChange({ field: 'visaAddon', value: e.target.checked })}
                color="primary"
                style={{ padding: 0, marginRight: 8 }}
              />
              <Typography>Visa</Typography>
            </div>
            {isSingaporeSelected && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={cruiseAddon || false}
                  onChange={(e) => onChange({ field: 'cruiseAddon', value: e.target.checked })}
                  color="primary"
                  style={{ padding: 0, marginRight: 8 }}
                />
                <Typography>Cruise</Typography>
              </div>
            )}
          </div>

          {/* Cruise Details Section */}
          {isSingaporeSelected && cruiseAddon && (
            <div className={classes.cruiseDetailsContainer}>
              <div className={classes.cruiseWarningBox}>
                <div className={classes.cruiseWarningHeader}>
                  <WarningIcon className={classes.cruiseWarningIcon} />
                  <Typography className={classes.cruiseWarningTitle}>
                    CRUISE DETAILS
                  </Typography>
                </div>
                <Typography className={classes.cruiseWarningText}>
                  Enter cruise name & duration to start building itinerary. You need to update sailing dates & costs before sending itinerary to client. Check the b2b cruise portal for details:
                </Typography>
                <div className={classes.cruisePortalInfo}>
                  <Typography className={classes.cruisePortalInfoText}>
                    <span style={{ fontWeight: 600 }}>Portal Link:</span>{' '}
                    <a
                      href="https://www.deckpro.in/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.cruisePortalLink}
                    >
                      https://www.deckpro.in/
                    </a>
                  </Typography>
                  <Typography className={classes.cruisePortalInfoText}>
                    <span style={{ fontWeight: 600 }}>Email:</span> tech@flynote.in
                  </Typography>
                  <Typography className={classes.cruisePortalInfoText}>
                    <span style={{ fontWeight: 600 }}>Password:</span> arktravelsystem
                  </Typography>
                </div>
              </div>

              <div className={classes.cruiseSelectionsColumn}>
                <div className={classes.cruiseSelectWrapper}>
                  <Typography className={classes.cruiseSelectLabel}>
                    CRUISE NAME
                  </Typography>
                  <FormControl variant="outlined" className={classes.cruiseSelectControl}>
                    <Select
                      value={cruiseName}
                      onChange={(e) => onChange({ field: 'cruiseName', value: e.target.value })}
                      className={classes.cruiseSelect}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select cruise
                      </MenuItem>
                      {cruiseNamesData?.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                <div className={classes.cruiseSelectWrapper}>
                  <Typography className={classes.cruiseSelectLabel}>
                    DURATION
                  </Typography>
                  <FormControl variant="outlined" className={classes.cruiseSelectControl}>
                    <Select
                      value={cruiseNights}
                      onChange={(e) => onChange({ field: 'cruiseNights', value: e.target.value })}
                      className={classes.cruiseSelect}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select duration
                      </MenuItem>
                      {(() => {
                        const maxNights = tripDates.from && tripDates.to
                          ? moment(tripDates.to).diff(moment(tripDates.from), 'days')
                          : Infinity;
                        return cruiseNightsData
                          ?.filter((duration) => {
                            const nights = parseInt(duration.split(' ')[0], 10);
                            return nights <= maxNights;
                          })
                          .map((duration) => (
                            <MenuItem key={duration} value={duration}>
                              {duration}
                            </MenuItem>
                          ));
                      })()}
                    </Select>
                  </FormControl>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={classes.customizationSection}>
          {!isMaldivesSelected && (
            <>
              <Typography className={classes.sectionLabel}>PACE OF TRIP</Typography>
              <div className={classes.optionButtonsContainer}>
                {['Relaxed', 'Balanced', 'Packed'].map((pace) => (
                  <ButtonBase
                    key={pace}
                    className={clsx(
                      classes.optionButton,
                      paceOfTrip === pace && classes.selectedOption
                    )}
                    onClick={() => onChange({ field: 'paceOfTrip', value: pace })}
                  >
                    <Typography className={classes.optionText}>
                      {pace}
                    </Typography>
                  </ButtonBase>
                ))}
              </div>
              <br />
              <br />
            </>
          )}
          <MultiSelect
            label="THEMES (Select all that apply) (Optional)"
            selections={tripTypes.items}
            value={tripTypes.value}
            extraClass={classes.themesSelector}
            inputClass={classes.input}
            data={tripTypesList}
            accessor="title"
            removeHandler={(item) => onRemoveHandler({ field: 'tripTypes', accessor: 'title', item })}
            onChange={(val) => onChange({ field: 'tripTypes', value: val })}
            onSelected={(item) => onItemSelection({ field: 'tripTypes', accessor: 'title', item })}
          />
          <br />
          <br />
          <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
            <MultiSelect
              inputClass={classes.input}
              label="OCCASSIONS (Optional)"
              selections={occasions.items}
              value={occasions.value}
              accessor="title"
              data={occasionList}
              removeHandler={(item) => onRemoveHandler({ field: 'occasions', accessor: 'title', item })}
              onChange={(val) => onChange({ field: 'occasions', value: val })}
              onSelected={(item) => onItemSelection({ field: 'occasions', accessor: 'title', item })}
            />
            <MultiSelect
              inputClass={classes.input}
              label="FOOD PREFERENCE"
              selections={foodPreference.items}
              value={foodPreference.value}
              accessor="title"
              data={foodPreferencesList}
              removeHandler={(item) => onRemoveHandler({ field: 'foodPreference', accessor: 'title', item })}
              onChange={(val) => onChange({ field: 'foodPreference', value: val })}
              onSelected={(item) => onItemSelection({ field: 'foodPreference', accessor: 'title', item })}
            />
          </div>
          <br />
          <br />
          <LabelInput
            label="CLIENT PREFERENCES / YOUR RECOMMENDATIONS (Optional)"
            extraClass={classes.clientPreferences}
            inputProps={{ multiline: true, rows: 4, rowsMax: 6 }}
            value={clientPreferences}
            placeholder="Talk about day wise itinerary, client requests or your suggestions like dining, activities, transfers, visas etc"
            onChange={(val) => onChange({ field: 'clientPreferences', value: val })}
          />

          <Footer
            errorMsg={errorMsg}
            extraClass={classes.footerFlexEnd}
          >
            <Button
              loading={isLoading}
              onClick={() => {
                if (!onValidityChecker(true, 'Customisations').error) {
                  // Navigate to Route Plan tab if enabled, otherwise submit directly
                  if (enableRoutePlan) {
                    if (onTabChange) {
                      onTabChange('Route Plan');
                    }
                  } else {
                    onSubmitHandler({ ignoreTemplate: true, isV3: true });
                  }
                }
              }}
            >
              Next
            </Button>
          </Footer>
        </div>
      </div>
    </div>
  );
};

Customisations.propTypes = {
  classes: PropTypes.object.isRequired,
  hotelCategory: PropTypes.string,
  paceOfTrip: PropTypes.string,
  tripTypes: PropTypes.shape({
    items: PropTypes.array,
    value: PropTypes.string,
  }).isRequired,
  tripTypesList: PropTypes.array.isRequired,
  occasions: PropTypes.shape({
    items: PropTypes.array,
    value: PropTypes.string,
  }).isRequired,
  occasionList: PropTypes.array.isRequired,
  foodPreference: PropTypes.shape({
    items: PropTypes.array,
    value: PropTypes.string,
  }).isRequired,
  foodPreferencesList: PropTypes.array.isRequired,
  mealPlan: PropTypes.shape({
    items: PropTypes.array,
    value: PropTypes.string,
  }).isRequired,
  mealPlanList: PropTypes.array.isRequired,
  transferMode: PropTypes.shape({
    items: PropTypes.array,
    value: PropTypes.string,
  }).isRequired,
  transferModeList: PropTypes.array.isRequired,
  clientPreferences: PropTypes.string,
  totalBudget: PropTypes.string,
  customBudget: PropTypes.string,
  visaAddon: PropTypes.bool,
  cruiseAddon: PropTypes.bool,
  splitStay: PropTypes.bool,
  cruiseName: PropTypes.string,
  cruiseNights: PropTypes.string,
  cruiseNamesData: PropTypes.array,
  cruiseNightsData: PropTypes.array,
  tripDates: PropTypes.shape({
    from: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    to: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }).isRequired,
  places: PropTypes.shape({
    items: PropTypes.array,
    value: PropTypes.string,
  }),
  errorMsg: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  itinerary: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  hasEdits: PropTypes.bool.isRequired,
  enableRoutePlan: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemoveHandler: PropTypes.func.isRequired,
  onItemSelection: PropTypes.func.isRequired,
  onValidityChecker: PropTypes.func.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

export default Customisations;
